import type { IRoleRepository } from '@yawi/domain';
import type { Role, CreateRoleRequest, UpdateRoleRequest, RolePermission } from '@yawi/types';
import type { PaginatedResponse, QueryParams } from '@yawi/domain';
import { getApiClient } from '../../http/apiClient';

export class ApiRoleRepository implements IRoleRepository {
  private readonly basePath = 'v1/roles';

  async getRoles(): Promise<Role[]> {
    const client = getApiClient();
    return await client.get<Role[]>(this.basePath);
  }

  async getRolesPaginated(params?: QueryParams): Promise<PaginatedResponse<Role>> {
    const client = getApiClient();
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.limit) queryParams.set('limit', String(params.limit));
    if (params?.search) queryParams.set('search', params.search);

    const queryString = queryParams.toString();
    const url = `${this.basePath}/paginated${queryString ? `?${queryString}` : ''}`;

    return await client.get<PaginatedResponse<Role>>(url);
  }

  async getRoleById(id: string): Promise<Role> {
    const client = getApiClient();
    return await client.get<Role>(`${this.basePath}/${id}`);
  }

  async createRole(data: CreateRoleRequest): Promise<Role> {
    const client = getApiClient();
    return await client.post<Role>(this.basePath, data);
  }

  async updateRole(id: string, data: UpdateRoleRequest): Promise<Role> {
    const client = getApiClient();
    return await client.put<Role>(`${this.basePath}/${id}`, data);
  }

  async deleteRole(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`${this.basePath}/${id}`);
  }

  async assignPermissions(id: string, permissions: RolePermission[]): Promise<Role> {
    const client = getApiClient();
    return await client.post<Role>(`${this.basePath}/${id}/permissions`, { permissions });
  }

  async getPermissions(id: string): Promise<RolePermission[]> {
    const client = getApiClient();
    return await client.get<RolePermission[]>(`${this.basePath}/${id}/permissions`);
  }
}
